import React, { useEffect, useRef, useState } from "react"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { Button } from "@/components/ui/Button"
import { SearchField } from "@/components/ui/SearchField"
import { Icon } from "@/components/icons/Icon"
import { GridSizeSlider } from "./ui/GridSizeSlider"
import { useEditor } from "@/utils/useEditor"
import { ExportDialog } from "@/components/Dialogs/ExportDialog"
import { ConfirmDialog } from "@/components/Dialogs/ConfirmDialog"
import { pushDialog, replaceDialog, resetDialogs } from "@/store/dialogs-state"
import {
	openCompareView,
	openFocusView,
	openGridView,
	photosViewAtom,
} from "@/store/photo/photos-view-state"
import {
	currentStyleAtom,
	isRemoteStyle,
	RemoteStyle,
	showAddEditsToStyleDialog,
	showStylePreviewDialog,
	Style,
} from "@/store/style/style-state"
import {
	isManualCullingAtom,
	loadingStateAtom,
	showingCullingEditTooltipAtom,
	showingEditingModeTooltipAtom,
} from "@/store/app-state"
import { cancelCalibration, endCalibration } from "@/store/style/calibration"
import {
	chooseProjectMode,
	clearProject,
	isProjectSetupIncomplete,
	blockSampleProjectExportIfNeeded,
	startEditing,
	startManualEditing,
	switchToCulling,
} from "@/store/project/project"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu"
import { Spinner } from "./ui/Spinner"
import {
	isCalibrationAtom,
	isCullingModeAtom,
	isHomeButtonEnabledAtom,
	isManualModeAtom,
	projectAtom,
} from "@/store/project/projects-state"
import {
	allPhotosAtom,
	anchorPhotosAtom,
	currentPhotoAtom,
	currentPhotoZoomAtom,
	displayGroupPhotosCountAtom,
	getRandomPhotosForStylePreview,
	isExpressSetupAtom,
	photosAtom,
	processingSelectedPhotosAtom,
	searchInputAtom,
	searchResultPhotoIdsAtom,
} from "@/store/photo/photos-state"
import { showingCropPanelAtom } from "../store/ui-views-state"
import { Select, SelectContent, SelectItem, SelectTrigger } from "./ui/Select"
import clsx from "clsx"

import { isPhotoLoadedIntoEditorAtom } from "@/store/editor/photo-loading"
import {
	currentAdjustmentsAtom,
	setHistoryIndex,
	undoableAdjustmentsHistoryAtom,
} from "@/store/store-history"
import { useShortcuts } from "@/utils/useShortcuts"
import { Adjustments, ViewMode } from "@/core/types"
import { FloatingTooltip } from "./ui/FloatingTooltip"
import { FinishCullingDialog } from "./Dialogs/FinishCullingDialog"
import {
	showChangeProjectDateDialog,
	showRenameProjectDialog,
} from "@/store/project/update-project"
import { switchStyle } from "@/store/style/switch-style"
import { ResetProjectDialog } from "./Dialogs/ProjectResetDialog"
import { DeleteProjectDialog } from "./Dialogs/ProjectDeleteDialog"
import toast from "react-hot-toast"
import {
	cullingAutoExposurePreviewUrlsAtom,
	currentCullingInfoAtom,
	extractFacesFromLoadedPhotoIfNeeded,
	generateAutoExposedPreview,
	isCullingAutoExposureOnAtom,
} from "@/store/importing/culling-state"
import {
	isPixiesetAuthEnabled,
	isViewReferencesDebugEnabled,
} from "@/utils/measurement/feature-flag"
import { PublishDialog } from "./Dialogs/PublishDialog"
import { SelectEditModeDialog } from "./Dialogs/SelectModeDialog"
import { projectItemToProjectInfo } from "@/store/project/projects-state"
import { StyleReferencesDialog } from "./Dialogs/StyleReferencesDialog"
import { StyleInfoDialog } from "./Dialogs/StyleInfoDialog"
import { useDebouncedValue } from "@/utils/useDebounce"
import { actionShortcuts } from "@/shortcuts/shortcuts"
import { store } from "@/store/store"
import { applyStyleToAllPhotos } from "@/store/style/apply-style"
import { StyleService } from "@/store/style/style-service"
import { PhotoRecord } from "@/lib/catalog"
import { useAndStoreRemoteStyleInProject } from "@/lib/catalog/helpers"
import { closeSession } from "@/routing/project-session"
import { useUserQuery } from "@/api/users-api"
import { isUserPartiallyInactive } from "@/api/dto/users"
import { showAccountPartiallyInactiveDialog } from "./Dialogs/SubscriptionWarningDialogs"
import { LoadingDialog } from "./Dialogs/LoadingDialog"
import { popActiveLoadingDialogs } from "@/store/loading-dialog-utils"
import { isNetworkConnectivityError, showActiveNetworkErrorToast } from "@/api/network-behavior"
import { useTranslation } from "react-i18next"
import { getLocalizedStyleDisplayName } from "@/i18n/style-display-name"

export const Toolbar = () => {
	const { t } = useTranslation("components")
	const editor = useEditor()
	const isHomeButtonEnabled = useAtomValue(isHomeButtonEnabledAtom)
	const history = useAtomValue(undoableAdjustmentsHistoryAtom)
	const previewState = useAtomValue(photosViewAtom)
	const currentPhoto = useAtomValue(currentPhotoAtom)
	const project = useAtomValue(projectAtom)
	const isPhotoLoaded = useAtomValue(isPhotoLoadedIntoEditorAtom)

	const [suggestedPhotos] = useAtom(anchorPhotosAtom)
	const [selectedProcessingPhotos] = useAtom(processingSelectedPhotosAtom)
	const [{ references: referenceCount }] = useAtom(displayGroupPhotosCountAtom)

	const isSettingUpProject = isProjectSetupIncomplete(project)
	const isCalibration = useAtomValue(isCalibrationAtom)
	const isManualMode = useAtomValue(isManualModeAtom)
	const isCullingMode = useAtomValue(isCullingModeAtom)
	const isExpressSetup = useAtomValue(isExpressSetupAtom)
	const isManualCulling = useAtomValue(isManualCullingAtom)

	const loadingState = useAtomValue(loadingStateAtom)
	const currentStyle = useAtomValue(currentStyleAtom)

	const currentProject = useAtomValue(projectAtom)
	const isCropActive = useAtomValue(showingCropPanelAtom)
	const isCompareEnabled = previewState == "focus" && !isCropActive

	const userQuery = useUserQuery()
	const user = userQuery.data

	const handleExportRequest = () => {
		if (currentProject?.isSampleProject && blockSampleProjectExportIfNeeded()) {
			return
		}
		replaceDialog(ExportDialog)
	}
	const handlePublishRequest = () => {
		if (!user) {
			toast.error(t("toolbar.errorLoadAccount"))
			return
		}

		if (isUserPartiallyInactive(user)) {
			showAccountPartiallyInactiveDialog()
			return
		}
		replaceDialog(PublishDialog)
	}

	const zoomLevels = [6, 12, 25, 33, 50, 66, 100, 200, 300, 400, 800, 1600] as const
	const [zoomLevel] = useAtom(currentPhotoZoomAtom)

	const lastViewMode = useRef<ViewMode>(editor.viewMode)

	const showingCullingEditTooltip = useAtomValue(showingCullingEditTooltipAtom)
	const showingEditingModeTooltip = useAtomValue(showingEditingModeTooltipAtom)

	const currentAdjustments = useAtomValue(currentAdjustmentsAtom)
	const cullingInfo = useAtomValue(currentCullingInfoAtom)
	const hasEdits = !!currentPhoto?.adjustmentsDigest
	const hasAutoExposure = !!cullingInfo?.luminanceScoreStats?.autoLightParams

	const [isAutoExposureOn, setIsAutoExposureOn] = useAtom(isCullingAutoExposureOnAtom)
	const [cullingAutoExposurePreviewUrls, setCullingAutoExposurePreviewUrls] = useAtom(
		cullingAutoExposurePreviewUrlsAtom,
	)

	// Search handling
	const [searchInputLocal, setSearchInputLocal] = useState("")
	const debouncedSearchInput = useDebouncedValue(searchInputLocal, 200)
	const [searchInput, setSearchInput] = useAtom(searchInputAtom)
	const allPhotos = useAtomValue(allPhotosAtom)
	const setSearchResultPhotoIds = useSetAtom(searchResultPhotoIdsAtom)
	const [isSearchExpanded, setIsSearchExpanded] = useState(false)
	const searchInputRef = useRef<HTMLInputElement>(null)
	const isFaceAutoExposureKeyPressed = useRef(false)

	useEffect(() => {
		if (isPhotoLoaded) {
			previewCurrentPhotoAutoExposure(isFaceAutoExposureKeyPressed.current)
		}
	}, [isPhotoLoaded])

	// Sync local search input with Atom(e.g., when switching between culling/editing modes)
	useEffect(() => {
		if (searchInput === "" && searchInputLocal !== "") {
			setSearchInputLocal("")
			setIsSearchExpanded(false)
		}
	}, [searchInput])

	useEffect(() => {
		setSearchInput(debouncedSearchInput)
	}, [debouncedSearchInput])

	useEffect(() => {
		if (!debouncedSearchInput.trim()) {
			setSearchResultPhotoIds(new Set())
			return
		}

		const searchTerms = debouncedSearchInput
			.split(",")
			.map((s) => s.trim().toLowerCase())
			.filter(Boolean)

		const matchedIds = new Set<string>()

		allPhotos.forEach((photo) => {
			const filename = photo.fileName.toLowerCase()

			for (const term of searchTerms) {
				if (filename.includes(term)) {
					matchedIds.add(photo.id)
					break
				}
			}
		})

		setSearchResultPhotoIds(matchedIds)
	}, [debouncedSearchInput, allPhotos])

	const openAndFocusSearch = () => {
		setIsSearchExpanded(true)
		setTimeout(() => searchInputRef.current?.focus(), 0)
	}

	useShortcuts({
		keys: [actionShortcuts.openPhotoSearch],
		action: (event) => {
			event.preventDefault()
			openAndFocusSearch()
		},
		trackingLabel: "expand_search",
		trackingValues: () => (currentProject?.id ? { projectId: currentProject.id } : undefined),
		disabled: isCalibration,
		preventDefault: true,
		enableOnFormTags: true,
		eventListenerOptions: { capture: true },
	})

	useShortcuts({
		keys: [actionShortcuts.faceAutoExposure],
		action: (event) => {
			if (event.repeat || event.key !== actionShortcuts.faceAutoExposure) {
				return
			}

			isFaceAutoExposureKeyPressed.current = event.type === "keydown"

			if (event.type === "keyup" && hasEdits) {
				return
			}

			previewCurrentPhotoAutoExposure(isFaceAutoExposureKeyPressed.current)
		},
		trackingLabel: "face_auto_exposure",
		trackingValues: () => (project?.id ? { projectId: project.id } : undefined),
		disabled: !hasAutoExposure || !isCullingMode,
		keyup: true,
		keydown: true,
	})

	useShortcuts({
		keys: [actionShortcuts.historyUndo],
		action: () => setHistoryIndex("undo"),
		trackingLabel: "history_undo",
		disabled: isCullingMode || (!history.canUndo && previewState === "grid" && !isPhotoLoaded),
		preventDefault: true,
	})

	useShortcuts({
		keys: [actionShortcuts.historyRedo],
		action: () => setHistoryIndex("redo"),
		trackingLabel: "history_redo",
		disabled: isCullingMode || (!history.canRedo && previewState === "grid" && !isPhotoLoaded),
		preventDefault: true,
	})

	const updateZoomLevel = (zoom: number | "fit") => {
		if (zoom === "fit") {
			editor.setViewport({
				offsetX: 0,
				offsetY: 0,
				zoom: "fit",
			})
		} else {
			editor.setViewport({ zoom })
		}
	}

	const toggleCompare = (event: React.PointerEvent) => {
		if (event.type == "pointerdown") {
			lastViewMode.current = editor.viewMode
			editor.setViewMode("before")
			event.currentTarget.setPointerCapture(event.pointerId)
		} else if (event.type == "pointerup") {
			editor.setViewMode(lastViewMode.current)
			event.currentTarget.releasePointerCapture(event.pointerId)
		}
	}

	const toggleAutoExposure = (event: React.PointerEvent) => {
		if (event.type == "pointerdown") {
			previewCurrentPhotoAutoExposure(true)
			event.currentTarget.setPointerCapture(event.pointerId)
		} else if (event.type == "pointerup") {
			previewCurrentPhotoAutoExposure(false)
			event.currentTarget.releasePointerCapture(event.pointerId)
		}
		event.stopPropagation()
	}

	const previewCurrentPhotoAutoExposure = (on: boolean) => {
		if (on === isAutoExposureOn) {
			return
		}
		if (!isPhotoLoaded || !currentPhoto || !project || !isCullingMode) {
			return
		}
		if (hasEdits) {
			toast.error(t("toolbar.errorAutoExposureWithEdits"), { duration: 3000 })
			return
		}

		setIsAutoExposureOn(on)

		let finalAdjustments: Adjustments = currentAdjustments

		if (on) {
			finalAdjustments = {
				...finalAdjustments,
				...cullingInfo?.luminanceScoreStats?.autoLightParams,
			}
		}

		editor.photo.adjustments = finalAdjustments
		editor.render({ reason: "auto-exposure-update", isForced: true, isDownsampled: false })

		if (on) {
			generateAutoExposedPreview(editor.photo.id)
		} else {
			for (const name in cullingAutoExposurePreviewUrls) {
				const url = cullingAutoExposurePreviewUrls[name]

				if (!url) {
					continue
				}

				URL.revokeObjectURL(url)
			}

			setCullingAutoExposurePreviewUrls({})
		}

		void extractFacesFromLoadedPhotoIfNeeded({
			projectId: project.id,
			photo: currentPhoto,
			loadedPhoto: editor.photo,
			isForAutoExposure: true,
		})
	}

	const showSwitchConfirmation = (switchStyle?: boolean) => {
		const title: string =
			isManualMode || switchStyle
				? t("toolbar.dialogSwitchStyleTitle")
				: t("toolbar.dialogSwitchTitle")

		const message: string =
			isManualMode || !switchStyle
				? t("toolbar.dialogSwitchMessage")
				: t("toolbar.dialogSwitchStyleMessage")
		return new Promise<void>((resolve) => {
			replaceDialog(ConfirmDialog, {
				title,
				message,
				secondary: { title: t("common:action.cancel") },
				primary: {
					title: t("toolbar.dialogSwitchPrimary"),
					isDestructive: true,
					handler: () => {
						resolve()
					},
				},
				trackingViewName: "switch_confirmation",
			})
		})
	}

	const showReapplyConfirmation = () => {
		return new Promise<void>((resolve) => {
			replaceDialog(ConfirmDialog, {
				title: t("toolbar.dialogReapplyTitle"),
				message: t("toolbar.dialogReapplyMessage"),
				secondary: { title: t("common:action.cancel") },
				primary: {
					title: t("toolbar.dialogReapplyPrimary"),
					isDestructive: false,
					handler: () => {
						resolve()
					},
				},
				trackingViewName: "reapply_confirmation",
			})
		})
	}

	const useDifferentStyle = async () => {
		if (currentProject && currentProject.handle) {
			await showSwitchConfirmation(true)
			void switchStyle()
		}
	}
	const useManualEditing = async () => {
		if (currentProject && currentProject.handle) {
			await showSwitchConfirmation()
			resetDialogs()

			//TODO: should we reset edits?
			const projectInfo = projectItemToProjectInfo(currentProject)
			await clearProject(projectInfo, ["edits"])

			if (currentProject && currentProject.handle) {
				void startManualEditing({
					handle: currentProject.handle,
					existingProject: currentProject,
				})
			}
		}
	}

	const addEditsToStyle = () => {
		showAddEditsToStyleDialog()
	}

	const showCurrentAIStyleDetail = () => {
		if (currentStyle) {
			pushDialog(StyleInfoDialog, { style: currentStyle })
		}
	}

	const showStyleReferences = () => {
		if (currentStyle) {
			pushDialog(StyleReferencesDialog, {
				style: currentStyle,
				trackingViewName: "style_references_dialog",
				fromProject: true,
			})
		}
	}

	const reapplyStyle = async () => {
		if (!currentStyle) {
			return
		}

		replaceDialog(LoadingDialog, { title: t("toolbar.fetchingAiStyleInfo") })

		try {
			const remoteStyle = await StyleService.fetchStyle(currentStyle.styleId, "active")
			const referenceImages = await StyleService.getReferencesForStyle(currentStyle.styleId)
			const style: RemoteStyle = new RemoteStyle(remoteStyle, referenceImages)
			style.globalBiases = currentStyle.globalBiases

			const photos = store.get(photosAtom)
			const previousPreviewPhotos = currentStyle.prioritizeImageIds
				?.map((id) => photos[id])
				.filter((photo): photo is PhotoRecord => Boolean(photo))

			const previewPhotos =
				previousPreviewPhotos && previousPreviewPhotos.length > 0
					? previousPreviewPhotos
					: getRandomPhotosForStylePreview()

			const applyStyle = async (style: Style) => {
				await showReapplyConfirmation()
				if (isRemoteStyle(style) && currentProject) {
					const projectInfo = projectItemToProjectInfo(currentProject)
					await clearProject(projectInfo, ["edits"])

					// eslint-disable-next-line react-hooks/rules-of-hooks
					const updatedStyle = await useAndStoreRemoteStyleInProject(style)
					applyStyleToAllPhotos(updatedStyle, true, "reapply_ai_style")
				}
				resetDialogs()
			}

			showStylePreviewDialog(
				{ trackingViewName: "reapply_style_dialog", canGoBack: false },
				style,
				"Reapply", // i18n-ignore: ProjectStyleTypes analytics value
				previewPhotos,
				applyStyle,
			)
		} catch (error) {
			popActiveLoadingDialogs()

			if (isNetworkConnectivityError(error)) {
				showActiveNetworkErrorToast()
			} else {
				toast.error(t("toolbar.errorLoadAiStyle"))
			}
		}
	}

	// const renameStyle = () => {
	// 	if (currentStyle) {
	// 		pushDialog(InputDialog, {
	// 			title: "Rename AI Style", // i18n-ignore
	// 			buttonTitle: "Update",
	// 			initialInput: currentStyle.name,
	// 			confirmWithEnter: true,
	// 			onConfirm: (name) => {
	// 				resetDialogs()
	// 				void updateAIStyleName(currentStyle, name)
	// 			},
	// 			trackingViewName: "rename_style_dialog",
	// 		})
	// 	}
	// }

	const renderManualEditView = () => {
		return (
			<div className="flex flex-row items-center space-x-2">
				<DropdownMenu>
					<DropdownMenuTrigger
						aria-label={t("toolbar.ariaSwitchToAiEditing")}
						className="ml-2 mr-4 h-5"
					>
						<div className="electron-no-drag flex flex-row items-center space-x-1 text-sm font-medium text-base-300 hover:text-base-50">
							<Icon name="manual" size={16} />
							<div>{t("toolbar.ariaManualEditing")}</div>
						</div>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						aria-label={t("toolbar.ariaSwitchToAiEditingMenu")}
						className="border-base-700 bg-base-800"
					>
						<DropdownMenuItem
							trackingLabel="use_different_style"
							aria-label={t("toolbar.ariaSwitchToDifferentAiStyle")}
							onClick={useDifferentStyle}
						>
							{t("toolbar.switchToAIStyle")}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		)
	}

	const renderProjectName = () => {
		const handle = currentProject?.handle
		const displayProjectName =
			currentProject?.isSampleProject || currentProject?.name === "Sample Project"
				? t("toolbar.sampleProject")
				: currentProject?.name
		if (currentProject && handle) {
			return (
				<DropdownMenu>
					<DropdownMenuTrigger className="electron-no-drag flex h-5 flex-row items-center space-x-1 overflow-hidden text-sm font-medium text-base-300 hover:text-base-50">
						<div className="max-w-[200px] truncate lg:max-w-[300px]">
							{displayProjectName}
						</div>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						aria-label={t("toolbar.ariaProjectMenu")}
						className="border-base-700 bg-base-800"
					>
						<DropdownMenuItem
							trackingLabel="rename_project"
							aria-label={t("toolbar.ariaRenameProject")}
							onClick={() => showRenameProjectDialog(currentProject)}
						>
							{t("toolbar.renameProject")}
						</DropdownMenuItem>
						<DropdownMenuItem
							trackingLabel="change_project_date"
							aria-label={t("toolbar.ariaChangeProjectDate")}
							onClick={() => showChangeProjectDateDialog(currentProject)}
						>
							{t("toolbar.adjustProjectDate")}
						</DropdownMenuItem>
						<DropdownMenuSeparator />
						<DropdownMenuItem
							trackingLabel="reset_project"
							aria-label={t("toolbar.ariaResetProject")}
							onClick={() =>
								replaceDialog(ResetProjectDialog, {
									project: currentProject,
									onFinish: (selected) => {
										if (selected.has("edits") && selected.has("ratings")) {
											// Asks to select mode again if everything is reset
											chooseProjectMode({
												handle: handle,
												existingProject: currentProject,
												disableClose: true,
											})
										} else if (selected.has("edits")) {
											if (isCullingMode) {
												toast.success(t("toolbar.toastEditsReset"))
											} else {
												// Asks to select edit mode again if edits are reset in editing mode
												replaceDialog(SelectEditModeDialog, {
													disableClose: true,
													onSelect: (mode) => {
														switch (mode) {
															case "style":
																void startEditing({
																	handle: handle,
																	existingProject: currentProject,
																	disableClose: true,
																})
																break
															case "manual_edit":
																void startManualEditing({
																	handle: handle,
																	existingProject: currentProject,
																})
																break
														}
													},
													trackingViewName: "select_edit_mode",
												})
											}
										} else if (selected.has("ratings")) {
											toast.success(t("toolbar.toastRatingsReset"))
										}
									},
								})
							}
						>
							{t("toolbar.resetProject")}
						</DropdownMenuItem>
						<DropdownMenuItem
							trackingLabel="switch_to_manual_editing"
							aria-label={t("toolbar.ariaSwitchToManualEditing")}
							disabled={isManualMode}
							onClick={useManualEditing}
						>
							{t("toolbar.switchToManualEditing")}
						</DropdownMenuItem>
						<DropdownMenuSeparator />
						<DropdownMenuItem
							trackingLabel="delete_project"
							aria-label={t("toolbar.ariaDeleteProject")}
							onClick={() =>
								replaceDialog(DeleteProjectDialog, { project: currentProject })
							}
						>
							<div>{t("toolbar.deleteProject")}</div>
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			)
		}
	}

	const renderStyleView = () => {
		const currentStyleDisplayName = currentStyle
			? getLocalizedStyleDisplayName(currentStyle.name, t)
			: ""

		return (
			<div className="flex flex-row items-center space-x-2">
				{currentStyle && !isCalibration && (
					<DropdownMenu>
						<DropdownMenuTrigger className="mx-2 h-5">
							<div className="electron-no-drag flex max-w-[180px] flex-row items-center space-x-1 truncate text-sm font-medium text-base-300 hover:text-base-50 lg:max-w-[250px]">
								{loadingState.status == "updatingProfile" && <Spinner size={16} />}
								{loadingState.status != "updatingProfile" && (
									<Icon name="style" size={16} />
								)}
								<div className="truncate">{currentStyleDisplayName}</div>
							</div>
						</DropdownMenuTrigger>
						<DropdownMenuContent
							aria-label={t("toolbar.ariaCurrentAiStyleMenu")}
							className="border-base-700 bg-base-800"
						>
							<DropdownMenuItem
								trackingLabel="add_edits_to_style"
								aria-label={t("toolbar.ariaAddCurrentEditsToAiStyle")}
								onClick={addEditsToStyle}
							>
								{t("toolbar.addFinalEditsToAIStyle")}
							</DropdownMenuItem>
							<DropdownMenuItem
								trackingLabel="tune_ai_style"
								aria-label={t("toolbar.ariaReapplyAiStyle")}
								onClick={() => reapplyStyle()}
							>
								{t("toolbar.reapplyAIStyle")}
							</DropdownMenuItem>
							<DropdownMenuItem
								trackingLabel="about_ai_style"
								aria-label={t("toolbar.ariaAboutCurrentAiStyle")}
								onClick={showCurrentAIStyleDetail}
							>
								{t("toolbar.aboutAIStyle")}
							</DropdownMenuItem>

							{isViewReferencesDebugEnabled() && (
								<DropdownMenuItem
									trackingLabel="show_style_references"
									aria-label={t("toolbar.ariaOpenAiStyleReferences")}
									onClick={showStyleReferences}
								>
									{t("toolbar.viewReferences")}
								</DropdownMenuItem>
							)}

							{/* <DropdownMenuItem
								trackingLabel="rename_current_style"
								onClick={renameStyle}
							>
								Rename
							</DropdownMenuItem> */}
							<DropdownMenuSeparator />
							<DropdownMenuItem
								trackingLabel="use_different_style"
								aria-label={t("toolbar.ariaSwitchToDifferentAiStyle")}
								onClick={useDifferentStyle}
							>
								{t("toolbar.useDifferentAIStyle")}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				)}
			</div>
		)
	}

	const renderSetupStyleButton = () => {
		return (
			<Button
				trackingLabel="finish_setup"
				aria-label={t("toolbar.ariaFinishSetup")}
				disabled={referenceCount < 1}
				variant="primary"
				onClick={() => {
					if (referenceCount >= suggestedPhotos.length) {
						endCalibration()
					} else {
						pushDialog(ConfirmDialog, {
							title: t("toolbar.dialogReferencesTitle"),
							message: t("toolbar.dialogReferencesMessage"),
							secondary: { title: t("common:action.cancel") },
							primary: {
								title: t("toolbar.dialogReferencesPrimary"),
								handler: endCalibration,
							},
							trackingViewName: "finish_setup_confirmation",
						})
					}
				}}
			>
				<div className="flex flex-row space-x-1">
					<Icon name="style" size={16}></Icon>
					<div>{t("toolbar.finishAiStyleSetup")}</div>
				</div>
			</Button>
		)
	}

	const renderSearchBar = () => {
		const shouldShowInput = isSearchExpanded || searchInputLocal.trim().length > 0

		return (
			<div className="relative flex items-center">
				{shouldShowInput ? (
					<SearchField
						ref={searchInputRef}
						placeholder={t("toolbar.placeholderSearchByFilename")}
						aria-label={t("toolbar.placeholderSearchByFilename")}
						value={searchInputLocal}
						onChange={setSearchInputLocal}
						onBlur={() => {
							if (!searchInputLocal.trim()) {
								setIsSearchExpanded(false)
							}
						}}
						autoFocus
					/>
				) : (
					<Button
						trackingLabel="expand_search"
						aria-label={t("toolbar.ariaExpandPhotoSearch")}
						trackingValues={
							currentProject?.id ? { projectId: currentProject.id } : undefined
						}
						variant="icon"
						size="custom"
						onClick={openAndFocusSearch}
						tooltip={t("toolbar.tooltipSearchPhotos")}
					>
						<Icon name="search" size={18} />
					</Button>
				)}
			</div>
		)
	}

	const renderExportButton = () => {
		if (isPixiesetAuthEnabled()) {
			return (
				<DropdownMenu>
					<DropdownMenuTrigger className="h-7 shrink-0 rounded-md bg-accent-600 pl-4 pr-2 text-sm hover:bg-accent-500">
						<div className="pr-0.5">{t("toolbar.exportPhotos")}</div>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						aria-label={t("toolbar.ariaExportMenu")}
						className="border-base-700 bg-base-800"
					>
						<DropdownMenuItem
							trackingLabel="publish_to_pixieset"
							aria-label={t("toolbar.ariaPublishPhotosToPixieset")}
							onClick={handlePublishRequest}
						>
							{t("toolbar.publishToPixieset")}
						</DropdownMenuItem>

						<DropdownMenuItem
							trackingLabel="export_local"
							aria-label={t("toolbar.ariaExportPhotosLocally")}
							onClick={handleExportRequest}
						>
							{t("toolbar.exportToLocal")}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			)
		} else {
			return (
				<Button
					trackingLabel="export"
					variant="primary"
					disabled={selectedProcessingPhotos.length > 0}
					onClick={handleExportRequest}
				>
					{t("toolbar.exportPhotos")}
				</Button>
			)
		}
	}

	const renderRightButtons = () => {
		if (isSettingUpProject) {
			return <div className="flex min-w-fit flex-1 flex-row justify-end space-x-2"></div>
		} else {
			return (
				<div className="flex min-w-fit flex-1 flex-row justify-end space-x-2">
					{!isCullingMode && isManualMode && renderManualEditView()}
					{!isManualMode && !isCullingMode && (
						<FloatingTooltip
							open={showingEditingModeTooltip}
							side="bottom"
							sideOffset={16}
							tooltip={{
								title: t("toolbar.tooltipStyleSettings"),
								main: t("toolbar.tooltipStyleSettingsMain"),
								index: 1,
								totalCount: 1,
								onboardingKey: "editingTooltipsShown",
							}}
						>
							{renderStyleView()}
						</FloatingTooltip>
					)}

					{!isCullingMode && (isManualMode || currentStyle) && (
						<Button
							trackingLabel="cull_photos"
							aria-label={t("toolbar.ariaSwitchToCulling")}
							trackingValues={
								currentProject?.id ? { projectId: currentProject.id } : undefined
							}
							variant="default"
							onClick={() => {
								if (currentProject) {
									switchToCulling(currentProject)
								}
							}}
						>
							{t("toolbar.cullPhotos")}
						</Button>
					)}
					{isCullingMode && (
						<FloatingTooltip
							open={showingCullingEditTooltip}
							side="bottom"
							sideOffset={16}
							tooltip={{
								title: t("toolbar.tooltipCullingFinish"),
								main: t("toolbar.tooltipCullingFinishMain"),
								index: isManualCulling ? 3 : 4,
								totalCount: isManualCulling ? 3 : 4,
								onboardingKey: isManualCulling
									? "manualCullingTooltipsShown"
									: "cullingTooltipsShown",
							}}
						>
							<Button
								trackingLabel="edit_photos"
								aria-label={t("toolbar.ariaSwitchToEditing")}
								trackingValues={
									currentProject?.id
										? { projectId: currentProject.id }
										: undefined
								}
								variant="primary"
								onClick={() => {
									replaceDialog(FinishCullingDialog)
								}}
							>
								{t("toolbar.editPhotos")}
							</Button>
						</FloatingTooltip>
					)}
					{!isCullingMode && isCalibration && !isExpressSetup && renderSetupStyleButton()}
					{!isCalibration && !isCullingMode && renderExportButton()}
				</div>
			)
		}
	}

	return (
		<div
			aria-label={t("toolbar.ariaTopToolbar")}
			className={clsx(
				"space-x-2",
				"electron-drag no-scrollbar flex h-[42px] w-full flex-none flex-row items-center justify-between overflow-x-auto border-b border-base-900 bg-base-800 px-2",
			)}
		>
			<div className="flex min-w-fit flex-1 flex-row items-center space-x-1">
				{(!isCalibration || isCullingMode) && (
					<Button
						trackingLabel="close_project"
						aria-label={t("toolbar.ariaGoBackToProjectsList")}
						variant="icon"
						size="custom"
						onClick={() => {
							void closeSession()
						}}
						disabled={!isHomeButtonEnabled}
					>
						<Icon className="flex flex-none" name="home" />
					</Button>
				)}

				{!isCullingMode && isCalibration && (
					<Button
						trackingLabel="exit_setup"
						aria-label={t("toolbar.ariaExitSetup")}
						variant="ghost"
						onClick={() => {
							pushDialog(ConfirmDialog, {
								canClose: true,
								title: t("toolbar.dialogExitSetupTitle"),
								message: t("toolbar.dialogExitSetupMessage"),
								primary: {
									title: t("toolbar.dialogExitSetupPrimary"),
									handler: cancelCalibration,
								},
								secondary: {
									title: t("toolbar.dialogExitSetupSecondary"),
									handler: useDifferentStyle,
								},
								trackingViewName: "exit_setup_confirmation",
							})
						}}
					>
						{t("common:action.cancel")}
					</Button>
				)}

				{renderProjectName()}
			</div>

			{previewState == "grid" && <GridSizeSlider />}

			{previewState !== "grid" && (
				<div
					role="group"
					aria-label={t("toolbar.ariaPhotoZoomControls")}
					className="flex flex-row items-center space-x-3"
				>
					<div
						role="button"
						tabIndex={0}
						aria-label={t("toolbar.ariaFitToWindow")}
						className={clsx(
							zoomLevel === "fit" ? "text-base-100" : "text-base-400",
							"electron-no-drag text-sm hover:text-base-300",
						)}
						onClick={() => updateZoomLevel("fit")}
					>
						{t("toolbar.fit")}
					</div>

					<Select
						trackingContext="tool_bar"
						trackingLabel="zoom_level"
						disabled={!isPhotoLoaded}
						value={String(zoomLevel)}
						onValueChange={(value) => {
							updateZoomLevel(Number(value) / 100)
						}}
					>
						<div className="flex flex-row items-center space-x-2">
							<div
								role="button"
								tabIndex={0}
								aria-label={t("toolbar.ariaZoomTo100")}
								className={clsx(
									zoomLevel === "fit" ? "text-base-400" : "text-base-100",
									"text-sm hover:text-base-300",
								)}
								onClick={() => updateZoomLevel(1)}
							>
								{zoomLevel === "fit" ? "100%" : Math.round(zoomLevel * 100) + "%"}
							</div>
							<SelectTrigger
								aria-label={t("toolbar.ariaSelectPhotoZoomLevel")}
								className="h-5 grow-0 border-0"
								onClick={(e) => e.currentTarget.blur()}
							></SelectTrigger>
						</div>
						<SelectContent className="bg-tertiary border-secondary/50 grow-0">
							{zoomLevels.map((level) => (
								<SelectItem key={level} value={String(level)}>
									<div>{level + "%"}</div>
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			)}

			{previewState !== "grid" && !isCullingMode && (
				<div className="flex w-9 flex-none items-center justify-center">
					<Button
						trackingLabel="view_before_after"
						aria-label={t("toolbar.ariaToggleBeforeAndAfterView")}
						disabled={!isCompareEnabled}
						variant="icon"
						size="custom"
						onPointerDown={toggleCompare}
						onPointerUp={toggleCompare}
						tooltip={t("toolbar.tooltipBeforeAfter")}
					>
						<Icon name="compare" size={17} />
					</Button>
				</div>
			)}

			<div className="flex flex-row">
				{!isCalibration && (
					<Button
						trackingLabel="open_gallery"
						aria-label={t("toolbar.ariaOpenGallery")}
						variant="icon"
						size="custom"
						isSelected={previewState == "grid"}
						onClick={openGridView}
						tooltip={t("toolbar.tooltipGallery")}
					>
						<Icon className="flex flex-none" name="grid_view" />
					</Button>
				)}
				<Button
					trackingLabel="open_focus"
					aria-label={t("toolbar.ariaOpenFocusView")}
					variant="icon"
					size="custom"
					disabled={!currentPhoto}
					isSelected={previewState == "focus"}
					onClick={openFocusView}
					tooltip={t("toolbar.tooltipFocus")}
				>
					<Icon className="flex flex-none" name="focus" />
				</Button>
				{!isCullingMode && (
					<Button
						trackingLabel="open_compare"
						aria-label={t("toolbar.ariaOpenPhotoCompare")}
						variant="icon"
						size="custom"
						disabled={!currentPhoto}
						isSelected={previewState == "before/after"}
						onClick={openCompareView}
						tooltip={t("toolbar.tooltipCompare")}
					>
						<Icon className="flex flex-none" name="splitscreen_right" />
					</Button>
				)}
			</div>

			{isCullingMode && (
				<Button
					className={clsx(
						"flex flex-row items-center space-x-1 bg-base-800 hover:bg-base-700 enabled:active:bg-base-300/100 enabled:active:text-base-900 disabled:pointer-events-none disabled:opacity-25",
						isAutoExposureOn && "bg-base-300 text-base-900 hover:bg-base-300/100",
					)}
					trackingLabel={"face_auto_exposure"}
					trackingValues={
						currentProject?.id ? { projectId: currentProject.id } : undefined
					}
					tooltip={t("toolbar.tooltipAutoExposure")}
					variant="icon"
					size="custom"
					onPointerDown={toggleAutoExposure}
					onPointerUp={toggleAutoExposure}
					onClick={(e) => e.stopPropagation()}
					disabled={hasEdits}
				>
					<Icon name="light" size={16} />
				</Button>
			)}

			{!isCalibration && renderSearchBar()}

			{renderRightButtons()}
		</div>
	)
}
